"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type CenteredCtaBlok = SbBlokData & {
  heading?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  bg_color?: unknown;
};

export default function CenteredCta({ blok }: { blok: CenteredCtaBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");

  const link = resolveLink(blok.button_link, region);
  const triggerAction = useTriggerAction();
  const action = blok.button_action || null;
  const showButton = Boolean(blok.button_label && (action || link.href !== "#"));

  // On a dark background a white button reads better than the purple one.
  const buttonClass = `mt-2 inline-flex h-14 w-fit items-center justify-center rounded-[8px] px-8 text-body font-bold transition-colors ${
    onDark
      ? "bg-white text-purple-05 hover:bg-purple-01"
      : "bg-purple-03 text-white hover:bg-purple-04"
  }`;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,6vw,6rem)]">
        <div className="mx-auto flex max-w-[620px] flex-col items-center gap-5 text-center">
          {blok.heading && (
            <h2
              className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                onDark ? "text-white" : "text-purple-05"
              }`}
            >
              {blok.heading}
            </h2>
          )}
          {blok.description && (
            <p className={`text-h6 leading-normal ${onDark ? "text-white/85" : "text-[#595959]"}`}>
              {blok.description}
            </p>
          )}
          {showButton &&
            (action ? (
              <button
                type="button"
                onClick={(e) => triggerAction(action, e)}
                className={`cursor-pointer ${buttonClass}`}
              >
                {blok.button_label}
              </button>
            ) : (
              <a
                href={link.href}
                {...(link.target ? { target: link.target } : {})}
                className={buttonClass}
              >
                {blok.button_label}
              </a>
            ))}
        </div>
      </div>
    </section>
  );
}
